/**
 * Agentic-surface auth — trimmed copy of frontend/src/features/auth/
 * AuthContext.tsx (the tenant-facing Cognito path), per ADR-0001 §2.2:
 * AWS Cognito with the SAME pool env vars (VITE_AWS_COGNITO_*) and the same
 * backend contract (backend/app/auth_utils.py validates the JWT via the
 * Cognito JWKS and resolves the tenant from the custom:tenant_id claim).
 * NOT Azure AD — that is admin/'s platform-staff SSO.
 *
 * Trimmed relative to frontend: no MFA modal, no dev auto-login, no Azure
 * SSO restore, no role preview. Kept: Cognito session restore + login,
 * localhost local-auth fallback (POST /api/auth/local/login, same as
 * frontend), token getter for the API client, fail-closed logout that
 * clears every auth artefact (SOC2 CC6.7).
 */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { AuthContext, type AuthUser, type LoginResult } from './context';
import { setGlobalTokenGetter } from './tokenStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Same pool + env var names as frontend/src/features/auth/AuthContext.tsx.
const poolData = {
  UserPoolId: import.meta.env.VITE_AWS_COGNITO_USER_POOL_ID || 'us-east-1_XTBQvp8b6',
  ClientId: import.meta.env.VITE_AWS_COGNITO_CLIENT_ID || '6tcmbkbikeejed0ggduv1jjsig',
};
const userPool = new CognitoUserPool(poolData);

const isLocalhost = () =>
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const shouldUseLocalAuth = () => import.meta.env.VITE_USE_LOCAL_AUTH === 'true' || isLocalhost();

function clearStoredAuth(): void {
  // SOC2 CC6.7 — flush every auth artefact on logout/expiry.
  try {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('tenant_name');
  } catch {
    // localStorage may be unavailable in sandboxed contexts.
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const cognitoSessionRef = useRef<CognitoUserSession | null>(null);
  const localTokenRef = useRef<string | null>(null);

  // Session restore on mount.
  useEffect(() => {
    if (shouldUseLocalAuth()) {
      // Local/dev: restore the local-auth session from localStorage (the
      // backend also honors its httpOnly cookie; the stored token is the
      // Authorization-header path).
      try {
        const token = localStorage.getItem('auth_token');
        const stored = localStorage.getItem('auth_user');
        if (token && stored) {
          localTokenRef.current = token;
          setUser(JSON.parse(stored) as AuthUser);
        }
      } catch {
        clearStoredAuth();
      }
      setLoading(false);
      return;
    }

    // Production: AWS Cognito session restore.
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setLoading(false);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        setLoading(false);
        return;
      }
      const jwt = session.getIdToken()?.getJwtToken();
      if (!jwt) {
        cognitoUser.signOut();
        setLoading(false);
        return;
      }
      cognitoSessionRef.current = session;
      const payload = session.getIdToken().payload as Record<string, string | undefined>;
      const email = payload.email || cognitoUser.getUsername();
      setUser({
        email,
        name: payload.name || payload.given_name || email.split('@')[0],
        tenant_uuid: payload['custom:tenant_id'],
      });
      setLoading(false);
    });
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    if (shouldUseLocalAuth()) {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/local/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
          credentials: 'include',
        });
        const body = (await res.json().catch(() => null)) as Record<string, string> | null;
        if (!res.ok) {
          return {
            success: false,
            error: (body && (body.detail || body.error)) || `Login failed (${res.status})`,
          };
        }
        if (!body?.access_token) return { success: false, error: 'Invalid server response' };
        const nextUser: AuthUser = {
          email: body.email,
          name: body.full_name || body.email,
          tenant_uuid: body.tenant_uuid,
          system_role: body.system_role,
        };
        localTokenRef.current = body.access_token;
        localStorage.setItem('auth_token', body.access_token);
        localStorage.setItem('auth_user', JSON.stringify(nextUser));
        setUser(nextUser);
        return { success: true, user: nextUser };
      } catch {
        return { success: false, error: 'Network error' };
      }
    }

    return new Promise<LoginResult>(resolve => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
        onSuccess: (session: CognitoUserSession) => {
          const jwt = session.getIdToken()?.getJwtToken();
          if (!jwt) {
            resolve({ success: false, error: 'Authentication token not ready' });
            return;
          }
          cognitoSessionRef.current = session;
          const payload = session.getIdToken().payload as Record<string, string | undefined>;
          const nextUser: AuthUser = {
            email: payload.email || email,
            name: payload.name || payload.given_name || email.split('@')[0],
            tenant_uuid: payload['custom:tenant_id'],
          };
          setUser(nextUser);
          resolve({ success: true, user: nextUser });
        },
        onFailure: (err: { code?: string; message?: string }) => {
          const message =
            err.code === 'UserNotFoundException' || err.code === 'NotAuthorizedException'
              ? 'Invalid credentials'
              : err.code === 'UserNotConfirmedException'
                ? 'Account not confirmed'
                : 'Login failed';
          resolve({ success: false, error: message });
        },
      });
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // best effort
    }
    userPool.getCurrentUser()?.signOut();
    cognitoSessionRef.current = null;
    localTokenRef.current = null;
    clearStoredAuth();
    setUser(null);
  }, []);

  const getAccessToken = useCallback((): string | null => {
    if (localTokenRef.current) return localTokenRef.current;
    const session = cognitoSessionRef.current;
    if (session) {
      try {
        const token = session.getIdToken()?.getJwtToken();
        if (token) return token;
      } catch {
        // fall through to the cached-session path
      }
    }
    // Race-condition fallback: read Cognito's cached session synchronously.
    try {
      const cached = userPool.getCurrentUser()?.getSignInUserSession();
      if (cached && cached.isValid()) return cached.getIdToken()?.getJwtToken() || null;
    } catch {
      // no cached session
    }
    return null;
  }, []);

  // Expose the getter to non-React layers (the API client).
  useEffect(() => {
    setGlobalTokenGetter(getAccessToken);
  }, [getAccessToken]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
};
